import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ForgotPasswordPage = () => {
  const [formData, setFormData] = useState({
    email: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);

  // API service class
  class AuthService {
    constructor() {
      this.baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';
    }

    async request(endpoint, options = {}) {
      const url = `${this.baseURL}${endpoint}`;
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      };

      try {
        const response = await fetch(url, config);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }

        return data;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    }

    async forgotPassword(email) {
      return this.request('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    }
  }

  const authService = new AuthService();

  // Countdown timer for resend button
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general errors
    if (errors.submit) {
      setErrors(prev => ({
        ...prev,
        submit: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await authService.forgotPassword(formData.email.trim().toLowerCase());
      
      if (response.success) {
        setIsEmailSent(true);
        setCanResend(false);
        setCountdown(60); // 60 seconds countdown before allowing resend
      }
      
    } catch (error) {
      console.error('Forgot password failed:', error);
      
      if (error.message?.includes('tidak ditemukan')) {
        setErrors({ 
          submit: 'Email tidak terdaftar. Pastikan Anda memasukkan email yang benar atau daftar akun baru.' 
        });
      } else {
        setErrors({ 
          submit: error.message || 'Terjadi kesalahan saat mengirim email reset password. Silakan coba lagi.' 
        });
      }
    }
    
    setIsLoading(false);
  };

  const handleResendEmail = async () => {
    if (!canResend) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await authService.forgotPassword(formData.email.trim().toLowerCase());
      
      if (response.success) {
        setErrors({ 
          submit: 'Email reset password berhasil dikirim ulang. Silakan cek email Anda.' 
        });
        setCanResend(false);
        setCountdown(60);
      }
      
    } catch (error) {
      console.error('Resend failed:', error);
      setErrors({ 
        submit: error.message || 'Gagal mengirim ulang email reset password.' 
      });
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <style>
        {` 
         .card-gradient {
            background: #ffffffcc;
            border: 1px solid rgba(30, 155, 76, 0.3);
            backdrop-filter: blur(10px);
          }
          
          .input-field {
            background: rgba(255, 255, 255, 0.7);
            border: 1px solid rgba(30, 155, 76, 0.4);
            transition: all 0.3s ease;
          }
          
          .input-field:focus {
            border-color: rgba(30, 155, 76, 0.7);
            box-shadow: 0 0 0 3px rgba(30, 155, 76, 0.2);
            background: #fff;
          }
          
          .btn-gradient {
            background: linear-gradient(135deg, #1E9B4C, #136d35);
          }
          
          .btn-gradient:hover {
            background: linear-gradient(135deg, #136d35, #1E9B4C);
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(30, 155, 76, 0.3);
          }

          .success-card {
            background: rgba(16, 185, 129, 0.1);
            border: 1px solid rgba(16, 185, 129, 0.3);
          }
        `}
      </style>
      
      {/* Hero Section */}
      <section className="hero-gradient py-20"
      style={{
        backgroundImage: "url('https://telnet.net.id/assets/bg.png')",
        backgroundColor:  '#dfe4e0ff',
        backgroundBlendMode: 'overlay',
      }}
      >
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <img
                src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
                alt="Icon Image"
                className="w-42 h-32 object-contain mx-auto"
                style={{ maxWidth: '70%' }}
            />
            <h1 className="text-4xl font-bold text-black mb-4">
              Reset Password
            </h1>
            <p className="text-xl text-black">
              {isEmailSent 
                ? "Kami telah mengirim link reset password ke email Anda"
                : "Masukkan email Anda untuk mendapatkan link reset password"
              }
            </p>
          </div>

          {!isEmailSent ? (
            /* Forgot Password Form */
            <div className="card-gradient rounded-3xl p-8 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Messages */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                )}

                {/* Email Field */}
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none"
                    placeholder="nama@email.com"
                    disabled={isLoading}
                    autoComplete="email"
                  />
                  {errors.email && (
                    <p className="text-red-600 text-sm">{errors.email}</p>
                  )}
                </div>

                {/* Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-blue-700 text-sm font-medium">Informasi Reset Password</p>
                      <p className="text-blue-600 text-sm mt-1">
                        Kami akan mengirim link reset password ke email Anda. Link tersebut berlaku selama 10 menit.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Mengirim email..." : "Kirim Link Reset Password"}
                </button>

                {/* Back to Login */}
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-[#1E9B4C] hover:underline font-semibold transition-colors inline-flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    <span>Kembali ke Login</span>
                  </Link>
                </div>
              </form>
            </div>
          ) : (
            /* Email Sent Confirmation */
            <div className="success-card rounded-3xl p-8 shadow-xl text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-4">Email Terkirim!</h2>
              
              <p className="text-gray-300 mb-6">
                Kami telah mengirim link reset password ke <strong className="text-white">{formData.email}</strong>. 
                Silakan cek email Anda dan klik link yang diberikan untuk reset password.
              </p>

              {/* Success Message */}
              {errors.submit && errors.submit.includes('berhasil') && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                  <p className="text-green-600 text-sm">{errors.submit}</p>
                </div>
              )}

              {/* Error Message for Resend */}
              {errors.submit && !errors.submit.includes('berhasil') && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                  <p className="text-red-600 text-sm">{errors.submit}</p>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-gray-100 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-800 mb-2">Langkah selanjutnya:</h3>
                <ol className="text-sm text-gray-600 text-left space-y-1">
                  <li>1. Buka email Anda dan cari email dari Telnet Indonesia</li>
                  <li>2. Klik link "Reset Password" di dalam email</li>
                  <li>3. Masukkan password baru Anda</li>
                  <li>4. Login dengan password baru</li>
                </ol>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={handleResendEmail}
                  disabled={isLoading || !canResend}
                  className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Mengirim...</span>
                    </div>
                  ) : !canResend ? (
                    `Kirim Ulang (${countdown}s)`
                  ) : (
                    'Kirim Ulang Email'
                  )}
                </button>
                
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 text-center"
                >
                  Kembali ke Login
                </Link>
              </div>

              {/* Help Text */}
              <p className="text-gray-400 text-sm mt-6">
                Tidak menerima email? Cek folder spam atau junk mail Anda.
              </p>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8">
            <div className="card-gradient rounded-2xl p-6 text-center">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Butuh bantuan?</h3>
              <p className="text-gray-600 text-sm mb-4">
                Jika Anda mengalami masalah dengan reset password, silakan hubungi tim customer service kami.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center space-x-2 text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>support@telnet.co.id</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>0800-1234-5678</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForgotPasswordPage;